import { useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentLanguageCode } from '@/shared/constants';
import { getContentLanguageLabel, sortLanguagesWithFrFirst } from '@/shared/utils/languageUtils';

interface LanguageTabsProps {
    activeLanguages: ContentLanguageCode[];
    currentLanguage: ContentLanguageCode;
    onLanguageChange: (lang: ContentLanguageCode) => void;
    onAddLanguage: () => void;
}

export const LanguageTabs: React.FC<LanguageTabsProps> = ({
    activeLanguages,
    currentLanguage,
    onLanguageChange,
    onAddLanguage,
}) => {
    const { t } = useTranslation();
    const tablistRef = useRef<HTMLDivElement>(null);
    const sortedLanguages = useMemo(
        () => sortLanguagesWithFrFirst(activeLanguages),
        [activeLanguages]
    );

    useEffect(() => {
        const node = tablistRef.current;
        if (!node) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.key !== 'ArrowLeft' &&
                event.key !== 'ArrowRight' &&
                event.key !== 'Home' &&
                event.key !== 'End'
            ) {
                return;
            }

            const tabs = Array.from(node.querySelectorAll<HTMLElement>('[role="tab"]'));
            if (tabs.length === 0) return;

            const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
            if (currentIndex < 0) return;

            event.preventDefault();
            let nextIndex = currentIndex;
            if (event.key === 'ArrowRight') {
                nextIndex = (currentIndex + 1) % tabs.length;
            } else if (event.key === 'ArrowLeft') {
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            } else if (event.key === 'Home') {
                nextIndex = 0;
            } else if (event.key === 'End') {
                nextIndex = tabs.length - 1;
            }

            tabs[nextIndex]?.focus();
            const lang = sortedLanguages[nextIndex];
            if (lang) {
                onLanguageChange(lang);
            }
        };

        node.addEventListener('keydown', handleKeyDown);
        return () => node.removeEventListener('keydown', handleKeyDown);
    }, [onLanguageChange, sortedLanguages]);

    return (
        <div
            className="language-tabs"
            ref={tablistRef}
            role="tablist"
            aria-label={t('exhibitions.chooseLanguage')}
        >
            {sortedLanguages.map((lang) => (
                <button
                    key={lang}
                    type="button"
                    role="tab"
                    aria-selected={currentLanguage === lang}
                    tabIndex={currentLanguage === lang ? 0 : -1}
                    className={`language-tab ${currentLanguage === lang ? 'active' : ''}`}
                    onClick={() => onLanguageChange(lang)}
                >
                    <span>{getContentLanguageLabel(lang)}</span>
                </button>
            ))}
            <div className="language-tab">
                <button
                    type="button"
                    className="add-language-btn"
                    onClick={onAddLanguage}
                    aria-label={t('common.add')}
                >
                    +
                </button>
            </div>
        </div>
    );
};
